import { EMPTY_FIELD } from 'constants/emptyField';
import { Resources } from 'constants/resources';

import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import { usePermissions } from 'hooks/usePermissions';
import { ReactComponent as DisableAccess } from 'images/disableAccess.svg';
import { MerchantShowAccessListFilter } from 'pages/Merchants/MerchantsShow/MerchantShowAccess/MerchantShowAccessListFilter';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import {
  ListContextProvider,
  RecordContextProvider,
  useListController,
  WithListContext,
} from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import { UserRoleChip } from 'shared/mui/Chips';
import { IconButton } from 'shared/mui/IconButtons';
import { PageHeader } from 'shared/mui/PageHeader';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import {
  DateTimeField,
  FunctionField,
  TextField,
  UserProfileField,
} from 'shared/react-admin/Fields';
import { Pagination } from 'shared/react-admin/Pagination/Pagination';
import { ActionsSlots } from 'shared/react-admin/Table/ActionsSlots/ActionsSlots';
import { Toolbar } from 'shared/react-admin/Toolbars/Toolbar';
import { MerchantAccess } from 'types/embedded/merchantAccess';
import { Permission } from 'types/embedded/permission';
import { UserRoleValue } from 'types/embedded/user';
import { getId } from 'utils';

import { MerchantShowAccessBulkDisableButton } from './components/MerchantShowAccessBulkDisableButton';
import { MerchantShowAccessCreateDialog } from './components/MerchantShowAccessCreateDialog/MerchantShowAccessCreateDialog';
import { MerchantShowAccessDisableDialog } from './components/MerchantShowAccessDisableDialog/MerchantShowAccessDisableDialog';
import { StyledDatagrid } from './MerchantShowAccessList.styled';
import { MerchantsShowActionSkeleton } from '../components/MerchantsShowActionSkeleton';

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantsShowAccessList: React.FC<Props> = ({ merchantId, merchantName }) => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions } = usePermissions();

  const listContext = useListController({
    resource: `${Resources.Merchants}/${getId(merchantId)}/merchant_accesses`,
    disableSyncWithLocation: true,
  });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDisableDialog, setOpenDisableDialog] = useState(false);
  const [selectedAccesses, setSelectedAccesses] = useState<MerchantAccess[]>([]);

  const hasEditPermission = hasResourcePermissions('merchant_accesses', [
    Permission.Full,
    Permission.Delete,
  ]);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        actions={[
          hasResourcePermissions('merchant_accesses', [Permission.Full, Permission.Create]) &&
            (listContext.isLoading ? (
              <MerchantsShowActionSkeleton />
            ) : (
              <Button
                key="create"
                label={translate('app.buttons.add')}
                onClick={() => setOpenCreateDialog(true)}
                startIcon={<ContentAdd />}
                variant="contained"
              />
            )),
        ]}
        headerTitle={{
          titleText: translate('app.merchantAccess.merchantAccessSettings'),
          titleSize: 'smallBold',
        }}
      />
      <WithListContext
        render={({ data, isLoading, filterValues }) => {
          const isShowAlert =
            ((data && data?.length <= 0) || !data) && !Object.keys(filterValues).length;

          if (isLoading) {
            return <DatagridSkeleton columnsCount={5} hasBulkActions={hasEditPermission} />;
          }

          if (isShowAlert) {
            return (
              <Alert severity="info">
                {translate('app.merchantAccess.noAccessAddedForMerchant', { merchantName })}.
              </Alert>
            );
          }

          return (
            <>
              <MerchantShowAccessListFilter />
              <StyledDatagrid
                bulkActionButtons={
                  hasEditPermission ? (
                    <MerchantShowAccessBulkDisableButton
                      setOpenDisableDialog={setOpenDisableDialog}
                      setSelectedAccesses={setSelectedAccesses}
                    />
                  ) : (
                    false
                  )
                }
              >
                <TextField label="ID" sortBy="id" source="originId" />
                <FunctionField
                  cellClassName="userInfo"
                  label={translate('app.table.user')}
                  render={(record: MerchantAccess) => {
                    return (
                      <RecordContextProvider value={record.user}>
                        <UserProfileField />
                      </RecordContextProvider>
                    );
                  }}
                />

                <FunctionField
                  label={translate('app.filter.role')}
                  render={(record: MerchantAccess) => {
                    return record?.user?.rolesValues?.length
                      ? record?.user?.rolesValues?.map((role: UserRoleValue, index) => (
                          <UserRoleChip key={index} roleValue={role} />
                        ))
                      : EMPTY_FIELD;
                  }}
                />
                <DateTimeField
                  label={translate('app.merchantAccess.assignmentDate')}
                  source="createdAt"
                />
                <ActionsSlots
                  render={(record: MerchantAccess) => (
                    <>
                      {hasEditPermission && (
                        <IconButton
                          onClick={() => {
                            setOpenDisableDialog(true);
                            setSelectedAccesses([record]);
                          }}
                        >
                          <DisableAccess />
                        </IconButton>
                      )}
                    </>
                  )}
                />
              </StyledDatagrid>
              <Toolbar style={{ paddingLeft: 0, paddingRight: 0 }}>
                <Pagination sx={{ width: '100%' }} />
              </Toolbar>
            </>
          );
        }}
      />
      <MerchantShowAccessCreateDialog
        merchantId={merchantId}
        onClose={() => setOpenCreateDialog(false)}
        open={openCreateDialog}
      />
      <MerchantShowAccessDisableDialog
        merchantId={merchantId}
        merchantName={merchantName}
        onClose={() => setOpenDisableDialog(false)}
        open={openDisableDialog}
        selectedAccesses={selectedAccesses}
      />
    </ListContextProvider>
  );
};
